import { castArray } from 'lodash';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import {
  Output,
  Component,
  OnDestroy,
  OnInit,
  EventEmitter,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { UntypedFormGroup, AbstractControl } from '@angular/forms';

import { SnFormFieldConfig } from '@lib/form';
import { TranslationService } from '@lib/translation';
import { t } from '@lib/translation/helpers';
import { SnUserFieldsGeneratorService } from '@lib/users';

import { ActivateService, ICreatePasswordResponse, AuthActivateStepStatus } from '../activate';

@Component({
  selector: 'sn-auth-create-password',
  templateUrl: './create-password.container.html',
  styleUrls: ['./create-password.container.scss'],
})
export class CreatePasswordContainer implements OnDestroy, OnInit {
  @Input()
  public uid: string;

  @Input()
  public token: string;

  @Output()
  public readonly created = new EventEmitter<ICreatePasswordResponse>();

  @Output()
  public readonly serverError = new EventEmitter<any>();

  public readonly form = new UntypedFormGroup({});
  public formFields: SnFormFieldConfig[] = [];

  public formModel: any = {};

  public label: string;

  public status: AuthActivateStepStatus;
  public translationsLoaded = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _userFieldsGeneratorService: SnUserFieldsGeneratorService,
    private readonly _activateService: ActivateService, // store the password rules in this service
    private readonly _translationService: TranslationService,
  ) {
    this._updateStatus(AuthActivateStepStatus.WAIT);
  }

  get rules(): string[] {
    return this._activateService.rules;
  }

  get isLoading(): boolean {
    return this.status === AuthActivateStepStatus.PROGRESS;
  }

  get newPasswordControl(): AbstractControl {
    return this.form.get('newPassword');
  }

  get confirmPasswordControl(): AbstractControl {
    return this.form.get('confirmPassword');
  }

  public ngOnInit(): void {
    this._translationService.translationReady$().pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.translationsLoaded = true;
      this.formFields = this._getFormFields();
      this._changeDetectorRef.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public createPassword(): void {
    if (this.form.invalid) {
      this._updateStatus(AuthActivateStepStatus.ERROR);

      return;
    }

    this._updateStatus(AuthActivateStepStatus.PROGRESS);

    this._activateService
      .createPassword(this.form.value.newPassword)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this._changeDetectorRef.markForCheck()),
      )
      .subscribe({
        next: (response) => {
          this._updateStatus(AuthActivateStepStatus.SUCCESS);
          this.created.emit(response);
        },
        error: (errorResponse: HttpErrorResponse) => {
          this._handlerServerError(errorResponse);
          this._updateStatus(AuthActivateStepStatus.ERROR);
        },
      });
  }

  private _updateStatus(status: AuthActivateStepStatus): void {
    this.status = status;

    switch (status) {
      case AuthActivateStepStatus.PROGRESS:
        this.label = t('account.dialogs.changePassword.newPassword.passwordCreationInProgress');
        break;

      case AuthActivateStepStatus.SUCCESS:
        this.label = t('account.dialogs.changePassword.newPassword.passwordCreated');
        break;

      default:
        this.label = t('account.dialogs.changePassword.newPassword.createYourPassword');
    }
  }

  private _getFormFields(): SnFormFieldConfig[] {
    return [
      this._userFieldsGeneratorService.getPasswordField({
        key: 'newPassword',
        templateOptions: {
          label: t('account.dialogs.changePassword.newPassword.label'),
          required: true,
          attributes: {
            autocomplete: 'nope',
          },
        },
      }),
      this._userFieldsGeneratorService.getPasswordField({
        key: 'confirmPassword',
        templateOptions: {
          label: t('account.dialogs.changePassword.newPassword.confirmPassword'),
          attributes: {
            autocomplete: 'nope',
          },
        },
        validators: {
          mismatchedPasswords: this._mismatchPasswordsHandler.bind(this),
        },
        validation: {
          messages: {
            mismatchedPasswords: "Passwords don't match",
          },
        },
      }),
    ];
  }

  private _mismatchPasswordsHandler(): boolean {
    return this.newPasswordControl.value === this.confirmPasswordControl.value;
  }

  private _handlerServerError(errorResponse: HttpErrorResponse): void {
    if (errorResponse instanceof HttpErrorResponse && errorResponse.status === 400) {
      this.serverError.next(errorResponse.error);
      Object.keys(errorResponse.error).forEach((key) => {
        const control = this.form.get(key);

        if (control) {
          const error = castArray(errorResponse.error[key]).join(' ');
          control.setErrors({ server: error });
        }
      });
    }
  }
}
